import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DatasourceFileResponseIntermediate, IFileDataSourceApiService } from 'interfaces-lib';
import { jwtDecode } from 'jwt-decode';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import { IdentityStorage } from 'storage-service-lib';

@Injectable({
  providedIn: 'root',
})
// TODO: Remove references to 'documents'
export class InveniasFileDataSourceApiService implements IFileDataSourceApiService {
  private readonly inveniasApiUrl: string;

  constructor(
    private http: HttpClient,
    private identityStorage: IdentityStorage,
  ) {
    if (!this.identityStorage.get()?.token) {
      throw new Error('Token missing. Sign-in may have failed.');
    }

    const jwtPayload = jwtDecode<any>((this.identityStorage.get()?.token as string));
    const endpointUrl = jwtPayload['http://www.invenias.com/claims/EndpointUrl'];
    this.inveniasApiUrl = `https://${endpointUrl}/api/v1`;
    console.debug('InveniasFileDataSourceApiService created with inveniasApiUrl', this.inveniasApiUrl);
  }

  // NOTE: fileType is 'document', but document type will be 'documents'
  postFile(file: File, fileParams: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${this.inveniasApiUrl}/${fileParams.entity.type}/${fileParams.entity.id}/${fileParams.fileType}`;
    return this.http.post<any>(url, formData).pipe(
      tap((resp) => console.log('Uploaded file to entity', resp)),
      concatMap((resp) =>
        this.getDocumentId(fileParams.entity.type, fileParams.entity.id, resp.AttachmentName).pipe(
          tap((docId) => console.log('id for document just saved to entity', docId)),
          map((docId) => ({
            id: docId,
            type: fileParams.fileType,
            name: resp.AttachmentName,
          })),
        ),
      ),
    );
  }

  checkFile(id: string): Observable<boolean> {
    const url = `${this.inveniasApiUrl}/documents/${id}/latestversion`;
    return this.http.get<any>(url).pipe(
      tap((resp) => console.log('Getting file ', resp)),
      map(() => true),
      catchError((err) => {
        if (err.status === 404) {
          return of(false);
        }
        throw err;
      }),
    );
  }

  getFileData(fileDatasourcePath: string[]): Observable<DatasourceFileResponseIntermediate> {
    const [_, entityType, entityId, fileId] = fileDatasourcePath;
    return this.getDocumentName(entityType, entityId, fileId).pipe(
      switchMap((docName) => {
        const url = `${this.inveniasApiUrl}/${entityType}/${entityId}/documents/${fileId}`;
        return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
          map((response) => {
            const contentType = response.headers.get('content-type') || 'application/octet-stream';
            const blob = response.body as Blob;
            return {
              contentType,
              fileBinaryData: blob,
              name: docName,
            };
          }),
        );
      }),
    );
  }

  private getDocumentName(entityType: string, entityId: string, fileId: string): Observable<string> {
    return this.queryDocument(entityType, entityId, {
      filter: ['ItemId', '=', fileId],
      select: ['AttachmentName'],
      resultField: 'AttachmentName',
      errorDescription: `id = '${fileId}'`,
    });
  }

  private getDocumentId(entityType: string, entityId: string, attachmentName: string): Observable<string> {
    return this.queryDocument(entityType, entityId, {
      filter: ['AttachmentName', '=', attachmentName],
      select: ['ItemId'],
      resultField: 'ItemId',
      errorDescription: `AttachmentName = '${attachmentName}'`,
    });
  }

  private queryDocument(
    entityType: string,
    entityId: string,
    options: {
      filter: string[];
      select: string[] | null;
      resultField: string;
      errorDescription: string;
    },
  ): Observable<string> {
    const url = `${this.inveniasApiUrl}/${entityType}/${entityId}/documents/list`;
    const requestBody: any = { Filter: options.filter };

    if (options.select) {
      requestBody.Select = options.select;
    }

    return this.http.post<any>(url, requestBody).pipe(
      tap((resp) => {
        if (resp.Items.length !== 1) {
          throw new Error(`No document or multiple documents with ${options.errorDescription}`);
        }
      }),
      map((resp) => resp.Items[0][options.resultField]),
    );
  }
}
